import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiErrorText, apiFetch, type Case, type RegistryOffice } from "../api.js";
import { useAuth } from "../auth.js";
import { useMe } from "../me.js";
import { AdminBreadcrumb } from "./AdminHome.js";
import { formatDate } from "./caseDisplay.js";

/**
 * THE OPS CASE QUEUE — the missing middle of the product.
 * ===========================================================================
 *
 * Until this existed a client could submit a case and no one could move it:
 * nothing in the web app called route or assign, so a SUBMITTED case had no path
 * to ROUTED and a verified attorney could never receive work. The whole funnel
 * ended in a table nobody was looking at.
 *
 * ORDERED OLDEST-TOUCHED FIRST, which is the opposite of what the API returns
 * (`listCasesForActor` orders by updatedAt DESC — newest first). That default is
 * right for a client watching their own case and wrong for an ops queue: newest
 * first buries the case nothing has happened to since March under the one someone
 * touched a minute ago. A queue's job is to surface what is STRANDED, so this
 * re-sorts ascending. The column header says which date it is sorting on, because
 * "oldest" is ambiguous between "opened longest ago" and "untouched longest" and
 * the operator should not have to guess which one they are looking at.
 *
 * WHAT THIS IS NOT: a directory. It lists CASES, and the only thing it says about
 * a professional is whether one is attached to a case — no name, no count, no
 * ordering by anything resembling merit (COMPLIANCE Constraint 1 applies inside
 * the admin panel too).
 */
export function AdminCases() {
  const { getToken } = useAuth();

  /**
   * The admin scope on /cases is EVERY case, unpaginated and unfiltered — there
   * is no query string the endpoint reads. So all grouping, counting and ordering
   * below happens client-side over this one array, and the counts are exact
   * rather than estimates.
   *
   * The key is shared with AdminRoster, which already invalidates ["admin",
   * "cases"] after a revocation. That matters: revoking a professional fires a
   * deferred DB trigger that moves their cases ASSIGNED -> ROUTED and clears the
   * assignee, with no API call from this page. A cached list would otherwise show
   * a stale ASSIGNED against an attorney who no longer has access.
   */
  const cases = useQuery({
    queryKey: ["admin", "cases"],
    queryFn: async () => apiFetch<{ cases: Case[] }>("/cases", { token: await getToken() }),
  });

  const offices = useQuery({
    queryKey: ["admin", "registry-offices"],
    queryFn: async () =>
      apiFetch<{ registryOffices: RegistryOffice[] }>("/admin/registry-offices", {
        token: await getToken(),
      }),
  });

  /**
   * A REAL GATE, not the usual courtesy — and this screen is the one place in
   * the admin portal where that distinction has teeth.
   *
   * Everywhere else, hiding an admin screen is cosmetic because the API refuses
   * the request anyway: a client who types /admin/roster gets the shell and a
   * row of 403s, which is honest. This screen was different, because its query
   * is `GET /api/cases` — a route that is role-SCOPED rather than admin-only. A
   * client asking for it is not refused; they are correctly served THEIR OWN
   * cases, with 200.
   *
   * So the failure was not a leak of anyone else's data. It was worse in a
   * quieter way: the page then wrapped that answer in "Every case on the
   * platform" and "The admin scope on this list is unfiltered", telling a client
   * in the product's own voice that they were looking at the whole platform. A
   * client with one case would conclude the platform had one case; a client with
   * none would conclude it was empty. The copy is right for an admin and a lie
   * for anyone else, and no server-side check can catch that — the server
   * answered the question it was asked, correctly.
   *
   * Found by e2e/identity-boundary.spec.ts, which is exactly the class of defect
   * a browser test exists to find and a unit test structurally cannot.
   */
  const me = useMe();
  if (me.data && me.data.role !== "admin") {
    return (
      <section className="wide">
        <header className="page-head">
          <p className="eyebrow">Internal operations</p>
          <h2>Case queue</h2>
        </header>
        <p role="alert">
          This screen is for platform operators. Your own cases are on your home
          screen — nothing here is missing from it.
        </p>
        <p>
          <Link href="/">Go back to your account</Link>
        </p>
      </section>
    );
  }

  return (
    <section className="wide">
      <AdminBreadcrumb />
      <header className="page-head">
        <p className="eyebrow">Internal operations</p>
        <h2>Case queue</h2>
        <p className="lede">
          Every case on the platform, least-recently-touched first, so a case
          nothing has happened to rises rather than sinks. Routing and assignment
          both start here.
        </p>
      </header>

      {cases.isPending ? <p className="muted" role="status">Loading the queue…</p> : null}

      {cases.error ? (
        <p role="alert">{apiErrorText(cases.error, "Could not load the case queue.")}</p>
      ) : null}

      {cases.data ? (
        cases.data.cases.length === 0 ? (
          /*
           * For an ADMIN specifically, an empty list means the platform genuinely
           * holds no cases — the admin scope has no where-clause at all. That is
           * why this sentence is safe to write here and would be a fabrication on
           * a client's or a professional's screen, where an empty list only means
           * "none scoped to you".
           */
          <p className="empty">
            No cases have been opened on the platform yet. This is the whole
            table, not a filtered view of it — the admin scope on this list is
            every case there is.
          </p>
        ) : (
          <>
            <Tally cases={cases.data.cases} />
            <CaseTable
              cases={cases.data.cases}
              officeNames={officeNameMap(offices.data?.registryOffices)}
              officesStatus={
                offices.isPending ? "loading" : offices.error ? "failed" : "ready"
              }
            />
          </>
        )
      ) : null}
    </section>
  );
}

/**
 * Registry office CODE -> NAME, for turning the case's routing key into
 * something legible. The case payload carries only the code.
 *
 * Returns null when the office list has not arrived, so the table can say why it
 * is showing a code alone instead of quietly rendering a bare code that looks
 * like the whole answer. Null means only "no map" — WHY there is no map is the
 * query's status, which this function does not see and must not guess at.
 */
function officeNameMap(list: RegistryOffice[] | undefined): Map<string, string> | null {
  if (!list) return null;
  return new Map(list.map((office) => [office.code, office.name]));
}

/**
 * What is waiting on ops, counted.
 *
 * These are counts of OUR OWN workflow states over the full array above — not
 * derived facts about anybody's land, and not estimates. The two that matter are
 * the ones where a case is waiting on an operator to do something; a case in a
 * work state is waiting on the attorney, which is not this page's business.
 */
function Tally({ cases }: { cases: Case[] }) {
  const awaitingRouting = cases.filter(
    (c) => c.status === "SUBMITTED" || c.status === "VALIDATING",
  ).length;
  const awaitingAttorney = cases.filter((c) => c.status === "ROUTED").length;

  return (
    <ul className="tally">
      <li>
        <strong>{awaitingRouting}</strong> awaiting routing{" "}
        <small>
          Submitted or validating. Routing has not yet resolved a registry office
          for these.
        </small>
      </li>
      <li>
        <strong>{awaitingAttorney}</strong> routed, no attorney yet{" "}
        <small>A registry office is known and the case is waiting to be assigned.</small>
      </li>
      <li>
        <strong>{cases.length}</strong> cases in total{" "}
        <small>Every case on the platform. The admin scope on this list is unfiltered.</small>
      </li>
    </ul>
  );
}

/**
 * Where the office list has got to. Three states, not two: the cases query and
 * the registry-offices query are independent and neither gates the other, so
 * "no map yet" is the NORMAL first paint of this screen and not a failure.
 * Collapsing loading into failed made every routed case in the queue assert an
 * error that had not happened (non-negotiable 1).
 */
type OfficesStatus = "loading" | "failed" | "ready";

function CaseTable({
  cases,
  officeNames,
  officesStatus,
}: {
  cases: Case[];
  officeNames: Map<string, string> | null;
  officesStatus: OfficesStatus;
}) {
  // Copied before sorting: the array belongs to the query cache and sorting in
  // place would mutate what every other reader of this key sees.
  const ordered = [...cases].sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));

  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label="Case queue">
      <table>
        <thead>
          <tr>
            <th>Case</th>
            <th>State</th>
            <th>Registry office</th>
            <th>Attorney</th>
            <th>Last activity</th>
            <th>Opened</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((caseRow) => (
            <tr key={caseRow.id}>
              <td>
                <code>{caseRow.id}</code>
              </td>
              <td>
                {/*
                  .state, never the .status-* palette. Those colours are a
                  judgement about a PERSON's verification standing. A case sitting
                  at VALIDATING or NEEDS_INFO is not bad news and the platform
                  holds no view on it — colouring it would invent a verdict.
                */}
                <span className="state">{caseRow.status}</span>
              </td>
              <td>
                <RegistryCell
                  code={caseRow.registryDistrict}
                  officeNames={officeNames}
                  officesStatus={officesStatus}
                />
              </td>
              <td>
                {/*
                  Whether an attorney is attached, and nothing else. No name, no
                  case count, no report count — a figure beside a person at the
                  moment work is being routed to them is a ranking, whatever it is
                  labelled (COMPLIANCE Constraint 1).
                */}
                {caseRow.assignedProfessionalId ? "Assigned" : "Nobody assigned"}
              </td>
              <td>{formatDate(caseRow.updatedAt)}</td>
              <td>{formatDate(caseRow.createdAt)}</td>
              <td>
                <Link href={`/cases/${caseRow.id}`}>Open</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The routing key, rendered without ever pretending to more than is known.
 *
 * Three genuinely different things, said differently:
 *  - null            -> not yet resolved. Never a blank cell: a blank in a table
 *                       of routing keys reads like an answer (non-negotiable 1).
 *  - code, name known -> the name, with the code beside it.
 *  - code, name not known -> the code alone, and WHY only the code. The office
 *    list is still arriving, or it failed, or the case carries a code with no
 *    matching row in the registry table. All three are worth an operator
 *    knowing about, they are DIFFERENT facts, and none licenses inventing a name.
 */
function RegistryCell({
  code,
  officeNames,
  officesStatus,
}: {
  code: string | null;
  officeNames: Map<string, string> | null;
  officesStatus: OfficesStatus;
}) {
  if (code === null) return <>Not yet resolved</>;

  const name = officeNames?.get(code);
  if (name) {
    return (
      <>
        {name} (<code>{code}</code>)
      </>
    );
  }

  return (
    <>
      <code>{code}</code> <span className="muted">{unnamedReason(officeNames, officesStatus)}</span>
    </>
  );
}

/**
 * Why the name is absent — the true reason, not the first one to hand.
 *
 * A map that ARRIVED and simply has no row for this code is a different finding
 * from no map at all, and is the only branch that says anything about the
 * registry table. The rest turn on where the query has got to; "did not load" is
 * reserved for a load that was actually attempted and actually failed.
 */
function unnamedReason(officeNames: Map<string, string> | null, status: OfficesStatus): string {
  if (officeNames !== null) return "— no office with this code is in the registry table";

  switch (status) {
    case "loading":
      return "— still loading the office list, so the name is not shown yet";
    case "failed":
      return "— office list did not load, so the name is not shown";
    case "ready":
      // Not pending and not an error, yet no list: nothing here knows why, so
      // this says that rather than picking whichever of the other two sounds
      // plausible.
      return "— the office list is not available here, and this page cannot tell why";
  }
}
